import { cn } from '@/lib/cn';
import type { SkillToolKey, ToolCallRecord } from '@innermate/shared';
import { Check, ChevronDown, ChevronRight, ExternalLink, Loader2, Table2, X } from 'lucide-react';
import { useState } from 'react';
import { SavedQueryPreviewModal } from './SavedQueryPreviewModal';

export interface ToolCallView {
  id: string;
  toolKey: SkillToolKey | string;
  status: 'pending' | 'ok' | 'error';
  summary: string | null;
  input?: unknown;
  output?: unknown;
  error?: string | null;
}

/**
 * 스트리밍 중인 assistant turn 의 이벤트 도착 순서를 보존하는 세그먼트.
 * delta 는 text 세그먼트로 쌓이고 tool_call_start 는 toolcall 세그먼트로 추가된다.
 * status 세그먼트는 서버에서 emit 하는 진행 상태 ("엑셀 파일 분석 중..." 등) 를 그대로 보존.
 * MessageBubble 이 이 배열을 순서 그대로 렌더해 DB 저장 후 모습과 동일한 시각 순서를 맞춘다.
 *
 * status 는 DB 에는 저장되지 않고 stream 중에만 존재. finally 에서 streamSegments 비워질 때
 * 함께 사라진다.
 */
export type StreamSegment =
  | { type: 'text'; value: string }
  | { type: 'toolcall'; call: ToolCallView }
  | { type: 'status'; message: string };

interface Props {
  call: ToolCallView;
  /** system.create_artifact / update_artifact 의 output.artifactId 가 있으면 표시되는 "패널 열기" 버튼 핸들러. */
  onArtifactClick?: (artifactId: string, version: number) => void;
}

function extractArtifactRef(call: ToolCallView): { artifactId: string; version: number } | null {
  // dashboard.create 도 artifact 를 만드는 도구라 패널 열기 chip 에 같이 노출.
  if (
    call.toolKey !== 'system.create_artifact' &&
    call.toolKey !== 'system.update_artifact' &&
    call.toolKey !== 'dashboard.create'
  ) {
    return null;
  }
  const out = call.output as { artifactId?: unknown; version?: unknown } | null | undefined;
  if (!out || typeof out !== 'object') return null;
  if (typeof out.artifactId !== 'string') return null;
  const v = typeof out.version === 'number' && out.version > 0 ? out.version : 1;
  return { artifactId: out.artifactId, version: v };
}

function extractSavedQueryRef(
  call: ToolCallView,
): { queryId: string; toolKey: 'db.save_query' | 'excel.save_query' } | null {
  if (call.toolKey !== 'db.save_query' && call.toolKey !== 'excel.save_query') return null;
  if (call.status !== 'ok') return null;
  const out = call.output as { queryId?: unknown } | null | undefined;
  if (!out || typeof out !== 'object') return null;
  if (typeof out.queryId !== 'string' || out.queryId.length === 0) return null;
  return { queryId: out.queryId, toolKey: call.toolKey };
}

interface GroundedSourcesView {
  sources: Array<{ title: string; url: string }>;
  truncated: boolean;
  filteredOutCount: number;
}

function extractGroundedSources(call: ToolCallView): GroundedSourcesView | null {
  if (call.toolKey !== 'web.search_grounded') return null;
  if (call.status !== 'ok') return null;
  const out = call.output as
    | { sources?: unknown; truncated?: unknown; filteredOutCount?: unknown }
    | null
    | undefined;
  if (!out || typeof out !== 'object') return null;
  if (!Array.isArray(out.sources)) return null;
  const sources: GroundedSourcesView['sources'] = [];
  for (const raw of out.sources) {
    if (!raw || typeof raw !== 'object') continue;
    const r = raw as { title?: unknown; url?: unknown };
    if (typeof r.url !== 'string' || r.url.length === 0) continue;
    sources.push({ title: typeof r.title === 'string' ? r.title : r.url, url: r.url });
  }
  if (sources.length === 0) return null;
  return {
    sources,
    truncated: out.truncated === true,
    filteredOutCount: typeof out.filteredOutCount === 'number' ? out.filteredOutCount : 0,
  };
}

function faviconUrl(targetUrl: string): string | null {
  try {
    const u = new URL(targetUrl);
    return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(u.hostname)}&sz=32`;
  } catch {
    return null;
  }
}

function formatJson(v: unknown): string {
  if (v === undefined || v === null) return '';
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

export function ToolCallChip({ call, onArtifactClick }: Props) {
  const [open, setOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  // HITL 인터럽트 도구 (ask_user / batch_ask_user) 는 chip 으로 노출 안 함.
  // 사용자 응답은 모달 + user 메시지로 이미 화면에 표시되므로 chip 은 중복 + 시스템 용어
  // ("자동 skip", "사용자 응답") 노출.
  if (call.toolKey === 'system.ask_user' || call.toolKey === 'system.batch_ask_user') {
    return null;
  }
  // Artifact 툴 오류는 스트리밍 패널이 시각 피드백을 담당하고 LLM 이 자동 재시도하므로 error chip 을 사용자에게 노출 안 함.
  if (
    call.status === 'error' &&
    (call.toolKey === 'system.create_artifact' ||
      call.toolKey === 'system.update_artifact' ||
      call.toolKey === 'dashboard.create')
  ) {
    return null;
  }
  const hasInput = call.input !== undefined && call.input !== null;
  const hasOutput = call.output !== undefined && call.output !== null;
  const hasError = call.status === 'error' && !!call.error;
  const expandable = hasInput || hasOutput || hasError;
  const artifactRef = extractArtifactRef(call);
  const savedQueryRef = extractSavedQueryRef(call);
  const groundedSources = extractGroundedSources(call);

  const Icon = call.status === 'pending' ? Loader2 : call.status === 'ok' ? Check : X;
  const iconCls =
    call.status === 'pending'
      ? 'text-foreground-muted animate-spin'
      : call.status === 'ok'
        ? 'text-emerald-600'
        : 'text-destructive';

  const label =
    call.status === 'pending'
      ? '실행 중…'
      : call.status === 'error'
        ? (call.error ?? call.summary ?? '실패')
        : (call.summary ?? '완료');

  return (
    <div className="flex flex-col gap-1 max-w-full">
      <div className="inline-flex items-center gap-1">
        <button
          type="button"
          onClick={() => expandable && setOpen((v) => !v)}
          disabled={!expandable}
          className={cn(
            'inline-flex items-center gap-1.5 h-7 px-2 rounded-md',
            'bg-surface-muted text-xs',
            expandable ? 'hover:bg-surface-muted/70 cursor-pointer' : 'cursor-default',
          )}
        >
          <Icon className={cn('size-3 shrink-0', iconCls)} />
          <span className="font-mono text-foreground-muted">{call.toolKey}</span>
          <span className="text-foreground truncate max-w-[28rem]">{label}</span>
          {expandable ? (
            open ? (
              <ChevronDown className="size-3 text-foreground-muted" />
            ) : (
              <ChevronRight className="size-3 text-foreground-muted" />
            )
          ) : null}
        </button>
        {artifactRef && onArtifactClick ? (
          <button
            type="button"
            aria-label="패널에서 열기"
            title={`v${artifactRef.version} 패널에서 열기`}
            onClick={() => onArtifactClick(artifactRef.artifactId, artifactRef.version)}
            className="inline-flex h-7 items-center gap-1 rounded-md px-1.5 text-xs text-foreground-muted hover:bg-surface-muted hover:text-foreground"
          >
            <ExternalLink className="size-3" />
            <span>패널</span>
          </button>
        ) : null}
      </div>
      {open && expandable ? (
        <div className="rounded-md border border-border bg-surface-muted/40 p-2 space-y-2">
          {groundedSources ? (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-foreground-muted mb-1">
                출처 ({groundedSources.sources.length}개
                {groundedSources.truncated ? ' · truncated' : ''}
                {groundedSources.filteredOutCount > 0
                  ? ` · 필터로 ${groundedSources.filteredOutCount}개 제외`
                  : ''}
                )
              </div>
              <ol className="space-y-1 list-decimal list-inside">
                {groundedSources.sources.map((s, i) => {
                  const fav = faviconUrl(s.url);
                  return (
                    <li
                      // biome-ignore lint/suspicious/noArrayIndexKey: sources 의 url 이 중복될 수 있어 index 도 함께 사용
                      key={`${s.url}-${i}`}
                      className="text-xs"
                    >
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 align-top hover:underline"
                      >
                        {fav ? (
                          <img
                            src={fav}
                            alt=""
                            width={14}
                            height={14}
                            className="size-3.5 rounded-sm shrink-0"
                          />
                        ) : null}
                        <span className="truncate max-w-[36rem] inline-block align-top">
                          {s.title}
                        </span>
                        <ExternalLink className="size-3 text-foreground-muted shrink-0" />
                      </a>
                    </li>
                  );
                })}
              </ol>
            </div>
          ) : null}
          {hasInput ? (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-foreground-muted mb-1">
                입력
              </div>
              <pre className="font-mono text-[11px] whitespace-pre-wrap break-all text-foreground/90">
                {formatJson(call.input)}
              </pre>
            </div>
          ) : null}
          {hasOutput ? (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-foreground-muted mb-1">
                출력
              </div>
              <pre className="font-mono text-[11px] whitespace-pre-wrap break-all text-foreground/90 max-h-80 overflow-auto">
                {formatJson(call.output)}
              </pre>
            </div>
          ) : null}
          {hasError ? (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-destructive mb-1">에러</div>
              <pre className="font-mono text-[11px] whitespace-pre-wrap break-all text-destructive">
                {call.error}
              </pre>
            </div>
          ) : null}
          {savedQueryRef ? (
            <div className="pt-1">
              <button
                type="button"
                onClick={() => setPreviewOpen(true)}
                className="inline-flex items-center gap-1.5 h-7 px-2 rounded-md bg-surface text-xs border border-border hover:bg-surface-muted/70"
              >
                <Table2 className="size-3" />
                <span>미리보기</span>
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
      {savedQueryRef ? (
        <SavedQueryPreviewModal
          queryId={savedQueryRef.queryId}
          toolKey={savedQueryRef.toolKey}
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
        />
      ) : null}
    </div>
  );
}

/** DB 의 ToolCallRecord → UI 의 ToolCallView 변환. */
export function toolCallRecordToView(record: ToolCallRecord): ToolCallView {
  return {
    id: record.id,
    toolKey: record.toolKey,
    status: record.status,
    summary: record.summary,
    input: record.input,
    output: record.status === 'ok' ? record.output : undefined,
    error: record.error,
  };
}
